<template>
	<div 
		class="component video-player" 
		:style="{ maxHeight: (forceMaxHeight && !isFullscreen) ? '500px' : '' }"
		:class="[
			'video-player-' + id, 
			{ 'auto-size' : autoSize && !isFullscreen }, 
			{ 'gif' : mediaType == 'gif' } 
		]"
		ref="observe"
	 >
		<div v-show="ready" style="overflow: hidden; border-radius: 6px;">
			<video 
				:id="'video-player-' + id" 
				ref="videoPlayer"
				class="video video-js skoften"
				>
			</video>
		</div>
		<div 
			v-show="!ready"
			class="poster uk-relative uk-text-center overflow-hidden" 
			:style="{ maxHeight: (forceMaxHeight && !isFullscreen) ? '500px' : '' }"
		>
			<img v-if="options.poster" :src="options.poster" :style="{ maxHeight: (forceMaxHeight && !isFullscreen) ? '500px' : '' }"/>
			<img v-else :src="(mediaType =='gif') ? defaultOptions.gifPoster : defaultOptions.poster" />
			
			<span class="uk-position-center" v-show="!ready">
				<loading-indicator></loading-indicator>
			</span>      
		</div>
		<div v-if="(hasProperties(metaData) || hasProperties(fallbackData)) && isRedactie && !isEmbed" class="meta-data uk-text-meta">
			<div class="uk-grid uk-margin-small-top">
				<div class="uk-width-auto" v-if="hasProperties(metaData)">
					Ratio: {{ getValue(metaData.width) }}x{{ getValue(metaData.height) }} 
					| Length: {{ metaData.length }}s 
					| Size: {{ fileSizeInMB }}MB 
					| <a :href="sourceUrl" target="_blank">Source</a> 
					| <a :href="options.sources[0].src" target="_blank">Encoded</a>
				</div>
				<div class="uk-width-expand uk-text-right" v-if="hasProperties(fallbackData)">
					<a :href="fallbackData.url" target="_blank" v-show="fallbackData.url">Fallback URL</a>
					<a href='' @click.prevent="toggleInfoBox(fallbackData.code)" v-show="fallbackData.code">Fallback CODE</a>
				</div>
			</div>
			<div v-show="fallbackInfo" class="uk-alert uk-alert-primary">{{ fallbackInfo }}</div>
		</div>
	</div>
</template>

<script>
import videojs from 'video.js'
import 'video.js/dist/video-js.css'

// Contrib ads
import 'videojs-contrib-ads'
import 'videojs-contrib-ads/dist/videojs-contrib-ads.css'

// Ima
import 'videojs-ima'
import 'videojs-ima/dist/videojs.ima.css'

// Mobile UI
import 'videojs-mobile-ui/dist/videojs-mobile-ui.css';
import 'videojs-mobile-ui';

// Other comppnents
import LoadingIndicator from './LoadingIndicator.vue'
import Cookies from 'js-cookie';
import { installVideoFallback } from '../js/videoFallback';

export default {
	name: 'VideoPlayer',
	components: { LoadingIndicator },
	props: {
		id: {
			default: Math.floor(Math.random() * 10000)
		},
		title: {
			type: String,
			default: '',
		},
		ads: {
			type: Object,
			default() {
				return {
					'website': false,
					'app': false
				};
			}		
		},
		adsLimit: { // number of ads per 24 hour, where 0 is unlimited
			type: Object,
			default() {
				return {
					'website': 0,
					'app': 0
				};
			}			
		},
		adsVdur: { // max time of ad
			type: Number,
			default: 60,
		},		
		adsWaterfall: {
			type: Boolean,
			default: false
		},
		adsVastUrls: {
			type: Array,
			default: () => ([
				{
					'name': 'google',
					'url': 'https://pubads.g.doubleclick.net/gampad/ads?iu=/21775744923/external/single_ad_samples&sz=640x480&cust_params=sample_ct%3Dlinear&ciu_szs=300x250%2C728x90&gdfp_req=1&output=vast&unviewed_position_start=1&env=vp&impl=s&correlator=', 
				},
				{ 
					'name': 'glomex',
					'url': 'https://glomex.github.io/vast-ima-player/linear-ad.xml', 
				}
			])
		},
		analytics: {
			type: Boolean,
			default: false
		},
		consentCheck: {
			type: Boolean,
			required: false,
			default: true
		},
		playsInline: {
		 type: Boolean,
		 default: false         
		},
		isEmbeddable: {
		 type: Boolean,
		 default: false     
		},
		isEmbed: {
		 type: Boolean,
		 default: false 
		},
		embedCode: {
			type: String,
			default: `<iframe src="https://www.site.com/video/id" width="560" height="315" frameborder="0"></iframe>`,
		},
		sourceUrl: {
			type: String,
			default: null
		},
		metaData: {
			type: Object,
			default: null,
		},
		options: {
			type: Object,
			default() {
				return {};
			}
		},
		viewportObserver: {
			type: Boolean,
			required: false,
			default: false,
		},
		viewport: {
			type: String,
			required: false
		},
		mediaType: { // mp4 or gif
			type: String,
		},
		autoSize: {
			type: Boolean,
			required: false,
			default: false
		},
		forceMaxHeight: {
			type: Boolean,
			required: false,
			default: false
		},
		fallbackData: {
			type: Object,
			default() {
				return {};
			}			
		},
		mobileUi: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			observer: null,
			player: null,
			shareModal: null,
			ready: false,
			isMuted: null,
			isPaused: null,
			isFullscreen: false,
			consentInterval: null,
			consentAttempts: 0,      
			defaultOptions: {
				autoplay: false,
				preload: "auto",
				playsinline: true,
				crossorigin: "anonymous",
				controls: true,
				experimentalSvgIcons: true,
				poster: 'https://placehold.co/800x450?text=video',
				gifPoster: 'https://placehold.co/600x400?text=GIF',
				techOrder: [ 'html5' ],
				controlBar: {
					fullscreenToggle: false
				},				
			},
			adReadyToStart: false,
			adIsPlaying: false,
			adCompleted: false,
			adSkipped: false,
			fallbackInfo: null,
			adsCurrentVastIndex: 0,
			userPressedPlay: null,	
		}
	},
	computed: {
		devMode() {
			return this.$root.devMode ?? false;
		},
		isRedactie() {
			return this.$root.user.isRedactie;
		},
		fileSizeInMB() {
			// Convert bytes to MB and fix to 2 decimal places
			return (this.metaData.filesize / 1024 / 1024).toFixed(2);
		},
		mergedOptions() {
			const defaultOptions = {
				autoplay: false,
				preload: "auto",
				playsinline: true,
				crossorigin: "anonymous",
				controls: true,
				experimentalSvgIcons: true,
				poster: 'https://placehold.co/800x450?text=video',
				gifPoster: 'https://placehold.co/600x400?text=GIF',
				techOrder: [ 'html5' ],
				controlBar: {
					fullscreenToggle: false
				},				
			};
			return { ...defaultOptions, ...this.options };
		}  
	},
	mounted() {     
		var app = this.$root;
		var component = this;
		var device = this.$root.device;
	
		// Add observer, this will init the video
		if(component.viewportObserver) {
			component.initObserver();
		} else if (component.consentCheck){
			component.startConsentCheck();
		} else {
			component.init();
		}		
	},
	beforeUnmount() {
		if (this.player) {
			this.player.dispose();
		}
		if(this.observer) {
			this.observer.disconnect();
		}
	},
	methods: {
		getValue: function(value) {
			// Check if value is null or undefined
			return value == null ? 'null' : value;
		},
		hasProperties: function(object) {
			
			if(object == null) {
				return false;
			}
			
			if(JSON.stringify(object).length > 0) {
				return true;
			} else {
				return false;
			}
		},
		showCMP() {
			//this.$root.showCMP();
		},
		hasReachedAdCountLimit: function() {
			var component = this;
			var type = (this.$root.device.app) ? 'app' : 'website';			
			let adCount = Cookies.get(`${type}AdCount`) || 0;
				
			if (component.adsLimit[type] === 0 || adCount < component.adsLimit[type]) {
				return false;
			} else {
				component.$logger('app/components/video-player/' + component.id + '/hasReachedAdCountLimit:true/' + component.id)
				return true;
			}
		},
		nextVastUrl: function() {
			
			var component = this;

			if (component.adsCurrentVastIndex < component.adsVastUrls.length - 1) {		
				component.adsCurrentVastIndex++;						
				var feed = component.adsVastUrls[component.adsCurrentVastIndex];	
	
				component.$logger('app/components/video-player/' + component.id + '/nextVastUrl/index: ' + component.adsCurrentVastIndex + '/name: ' + feed.name);
								
				return feed.url;
			} else {
					return null; // No more ads to try
			}
		},
		init: function() {

			var component = this;
		 
			// Merged options
			const mergedOptions = Object.assign({}, component.defaultOptions, component.options);
			
			component.$logger('app/components/video-player/' + component.id + '/init/' + component.id)				
			//component.$logger(mergedOptions);
										
			// Create player instance
			component.player = videojs(component.$refs.videoPlayer, mergedOptions, () => {
				
				component.$logger('app/components/video-player/' + component.id + '/ready')
				
				setTimeout(function() {
					component.ready = true;
				}, 200);
				
				// Muted?
				component.setMutedOrNot();
	
				// Start?
				if(component.options.autoplay && !component.adIsPlaying) {
					console.log('joejoe autoplay')
					setTimeout(function() {
						component.player.play();
					}, 50);
				}
			});
							
			installVideoFallback(component.player);

			// VAST init
			if(
				(component.ads.website || component.ads.app) && 
				(!component.$root.user.loggedIn || component.$root.user.isRedactie)	&&
				!component.hasReachedAdCountLimit() && 
				typeof google != "undefined" && 
				typeof component.player.ima != "undefined" 
			) {
				component.$logger('app/components/video-player/' + component.id + '/ads-enabled')
				component.initIMA();
			}
					
			// Add event listeners
			component.player.on('pause', component.handlePause);
			component.player.on('volumechange', component.handleVolumeChange);
			component.player.on('play', component.handlePlay);
			component.player.on('ended', component.handlePlay);
			component.player.on('fullscreenchange', component.handleFullscreen);
	
			// Track manual play event
			component.player.bigPlayButton.on('click', function(){
				component.userPressedPlay = true;
			});
				
			// Add custom fullscreen icon so we can handle it our selves (required for android vs ios)
			component.addCustomFullScreenButton();
						
			// Add embed button
			if(component.isEmbeddable) {
				component.addEmbedButton();
			}
			
			// Mobile UI plugin
			if(component.mobileUi) {
				component.player.mobileUi(
					{
						fullscreen: {
							enterOnRotate: true,
							exitOnRotate: true,
							lockOnRotate: true,
							lockToLandscapeOnEnter: false,
							disabled: false
						},
						touchControls: {
							seekSeconds: 10,
							tapTimeout: 300,
							disableOnEnd: false,
							disabled: false,
						}
					}
				);
			}
		}, 
		initObserver: function() {
			
			var component = this;
	 
			component.$logger('app/components/video-player/init-observer/' + component.id);
			
			const options = {
				root: null,
				rootMargin: '-30% 0% -30% 0%',
				threshold: 0.5
			};
			
			// Large rootMargin
			if(component.viewport == "large") {
				options.rootMargin = '50% 0% 50% 0%';
			}
			
			// Large rootMargin when video not ready
			if (!component.ready) {
				//options.rootMargin = '50% 0% 50% 0%';
			}
			
			var elementToObserve = this.$refs.observe;
	
			component.observer = new IntersectionObserver((entries) => {
				 entries.forEach(entry => {
					 if (entry.isIntersecting) {
						 
						 // Not ready
						 if (!component.ready) {

							// Consent check   
							if(component.ads && 
								component.consentCheck && 
								typeof google != 'undefined') 
							{
								// If consent fails, it will init the player
								component.startConsentCheck();
							} else {     
								component.init();
							}
							
						 // Player ready
						 } else {
							 
							// Mute or not, depends on some variables and devices settings
							 component.setMutedOrNot();

							if(!component.adIsPlaying) {
								setTimeout(function() {
									component.player.play();
								},100);
							}
							
						 }
					 } else {
						 if (component.ready) {
								component.player.pause();
						 }
					 }
				 });
			}, options);
					
			component.observer.observe(elementToObserve); 
		},
		initIMA: function() {
			
			var component = this;

			if(typeof component.player.ima == "undefined") {
				return;
			}
			
			// Initial VAST Url
			var initialIndex = 0;
			if(component.$helper.getURLParameter('vastIndex') != null) {				
				initialIndex = component.$helper.getURLParameter('vastIndex');
				component.adsCurrentVastIndex = initialIndex;
			}
			
			console.log('initialIndex: ' + initialIndex)
			
			var initialAdUrl = component.adsVastUrls[initialIndex].url;
			initialAdUrl = component.addExtraParameters(initialAdUrl);
			
			// Ima options
			var imaOptions = {
				adTagUrl: initialAdUrl,
				disableAdControls: false,
				adLabel: component.adLabel ?? 'promo',
				numRedirects: 10,
				debug: component.devMode
			};
						
			// Ad event: adsready
			component.player.on("adsready", function (e) {
							
				component.$logger('app/components/video-player/' + component.id + '/ads/ready (feed: ' + component.adsVastUrls[component.adsCurrentVastIndex].name + ')');
				
				component.adReadyToStart = true;
			
				component.player.ima.addEventListener(google.ima.AdEvent.Type.LOADED, function(event) {
						var ad = event.getAd();						
						var adSystem = ad.getAdSystem();
						var clickThroughUrl = ad.data.clickThroughUrl;
						
						component.$logger('app/components/video-player/' + component.id + '/ads/loaded/adSystem/' + adSystem);
						component.$logger('app/components/video-player/' + component.id + '/ads/loaded/clickUrl/' + clickThroughUrl);			
						component.$logger(JSON.stringify(ad));
				});
									
				// Ad event: started
				component.player.ima.addEventListener(google.ima.AdEvent.Type.STARTED, function (event) {
					component.$logger('app/components/video-player/' + component.id + '/ads/started');
					component.adIsPlaying = true;
					
					// Update cookie ad count for app or website
					var type = (component.$root.device.app) ? 'app' : 'website';
					let adCount = Cookies.get(`${type}AdCount`) || 0;
					
					Cookies.set(`${type}AdCount`, ++adCount, { expires: 1 });
				});	
				
				// Ad event: ended/completed
				component.player.ima.addEventListener(google.ima.AdEvent.Type.COMPLETE, function () {
					component.$logger('app/components/video-player/' + component.id + '/ads/completed');
					component.adIsPlaying = false;
					component.adCompleted = true;
				});	

				// Ad event: skipped
				component.player.ima.addEventListener(google.ima.AdEvent.Type.SKIPPED, function () {
					component.$logger('app/components/video-player/' + component.id + '/ads/skipped');
					component.adIsPlaying = false;
					component.adSkipped = true;
				});					
			});
			
			// Ad event: adsError
			component.player.on("adserror", function (e) {
				
				component.$logger('app/components/video-player/' + component.id + '/ads/error: ' + e.data.AdError.data.errorMessage);
				component.$logger('app/components/video-player/' + component.id + '/ads/error/feed: ' + component.adsVastUrls[component.adsCurrentVastIndex].name + ' has no valid response');
				component.$logger(e);
				
				// Get next vast url
				if(component.adsWaterfall && component.$helper.getURLParameter('vastIndex') == null) {
					var nextUrl = component.nextVastUrl();
					if (nextUrl) {
						
						component.$logger('app/components/video-player/' + component.id + '/ads/changeAdTag/url: ' + nextUrl);
						
						nextUrl = component.addExtraParameters(nextUrl);
						component.player.ima.changeAdTag(nextUrl);
						component.player.ima.requestAds();
												
					} else {
						component.$logger('app/components/video-player/' + component.id + '/ads/error/all-feeds-failed-to-deliver');
					}
				}
			});
											
			// Init ima	
			component.player.ima(imaOptions);    
			
			// Remove controls from the player on iPad to stop native controls from stealing
			// our click
			var contentPlayer =  document.getElementById('video-player-' + component.id + '_html5_api');
			if ((navigator.userAgent.match(/iPad/i) ||
					navigator.userAgent.match(/Android/i)) &&
					contentPlayer.hasAttribute('controls')) {
				contentPlayer.removeAttribute('controls');
			}
			
			// Initialize the ad container when the video player is clicked, but only the
			// first time it's clicked.
			var initAdDisplayContainer = function() {
				component.player.ima.initializeAdDisplayContainer();
				wrapperDiv.removeEventListener(startEvent, initAdDisplayContainer);
			}
			
			var startEvent = 'click'; 
			if (navigator.userAgent.match(/iPhone/i) ||
					navigator.userAgent.match(/iPad/i) ||
					navigator.userAgent.match(/Android/i)) {
				startEvent = 'touchend';
			}
			
			var wrapperDiv = document.getElementById('video-player-' + component.id);
			wrapperDiv.addEventListener(startEvent, initAdDisplayContainer);
							
		},
		setMutedOrNot: function() {
			
			// Mute when user has manually muted a player
			// or when we're on mobile, because mobile won't allow autoplay with sound
			// or when where's playing a ad and user has not interacted with the DOM
			
			var component = this;
			
			if(component.options.muted) {
				component.player.muted(true);
			} else if(Cookies.get('videoPlayerMuted') == 'true' && component.userPressedPlay == false) {
				component.player.muted(true);
			} else if(component.$root.device.mobile && component.options.autoplay) {
				component.player.muted(true);
			} else if(!window.userInteracted && component.options.autoplay) {
				component.player.muted(true);
			} else if (!window.userInteracted && component.adReadyToStart) { 
				component.player.muted(true);
			} else {
				component.player.muted(false);
			}		
		},
		canAutoPlay: function(string) {
			var component = this;
			var promise = component.player.play();
		
			if (promise !== undefined) {
				promise.then(function() {
				}).catch(function(error) {
				});
			}
		},
		handleFullscreen: function(event) {
			var component = this;
			this.$logger('app/component/video-player/' + component.id + '/handleFullscreen');
						
			// Disable forceMaxHeight when people click fullscreen
			if(this.player.isFullscreen()) {
				this.isFullscreen = true;
			} else {
				this.isFullscreen = false;
			}
		},
		handleVolumeChange: function() {
			this.isMuted = this.player.muted();
			// Store in cookie so we can check it on other videoplayer in the page
			Cookies.set('videoPlayerMuted', (this.isMuted) ? 'true' : 'false', { expires: 365 });
		},   
		handlePause: function() {
			this.$logger('app/components/video-player/' + this.id + '/event:pause');			
			this.isPaused = true;
		},
		handlePlay: function() {
			
			var component = this;
			
			if(component.ads && 
				component.adsCurrentVastIndex > 0 && 
				!component.adCompleted &&
				!component.adSkipped) 
			{
				component.player.ima.requestAds();
			}
			
			this.$logger('app/components/video-player/' + this.id + '/event:play');			
			this.isPaused = false;
			
			// Track event
			if(this.analytics) {
				//component.trackEvent('play');
			}
		},     
		handleEnded: function() {
			this.$logger('app/components/video-player/' + this.id + '/event:ended');

			// Track event
			if(this.analytics) {
				//component.trackEvent('completed');
			}	
		},  	
		trackEvent: function(eventName) {

			if(typeof gtag == "undefined") return;

			var component = this;
	
			var trackingData = {
				id: component.id,
				event: eventName,
				url: window.location.href,
				title: component.title
			};
			
			gtag('event', 'videos', trackingData);
									
			component.$logger('app/components/video-player/track:' + eventName + '/' + component.id);
		}, 
		addEmbedButton: function() {

			var component = this;
			
			// Create Button 
			const myButton = component.player.getChild('ControlBar').addChild('button', {
				controlText: '',
				className: 'vjs-visible-text',
				clickHandler: function(event) {  
					
					// Open Modal        
					component.shareModal.open();
					
					// Attach click listener so we can copy the code to clipboard
					var embedButton = document.getElementById('video-embed-button-' + component.id);
					var embedCode = document.getElementById('video-embed-code-' + component.id);
					
					embedButton.addEventListener('click', () => {
						embedCode.select();
						document.execCommand('copy');
					});
				}
			});
			
			// Set icon
			myButton.setIcon('share');
			
			// Create Modal overlay
			const ModalDialog = videojs.getComponent('ModalDialog');
		 
			var modalHtmlContent = document.createElement('div');
			modalHtmlContent.innerHTML = `
				<div class="vjs-embed-modal uk-padding">
					<div class="uk-position-center">
						<h4>Video embedden</h4>
						<div class="uk-grid uk-grid-small">
							<div class="uk-width-3-4">
								<input type="text" class="uk-input uk-width-1-1" id="video-embed-code-${component.id}" value='${component.embedCode}'>
							</div>
							<div class="uk-width-expand">
								<a class="uk-button uk-button-primary" id="video-embed-button-${component.id}">kopiëren</a>
							</div>
						</div>
					</div>
				</div>
			`;
		
			component.shareModal = new ModalDialog(component.player, {
				temporary: false,
				content: modalHtmlContent
			});
						
			component.player.addChild(component.shareModal);
					 
		},    
		addCustomFullScreenButton: function() {
	
			var component = this;
			
			// Create Button 
			const myButton = component.player.getChild('ControlBar').addChild('button', {
				controlText: '',
				className: 'vjs-visible-text',
				clickHandler: function(event) {  
					
					var currentTime = component.player.currentTime() || 0;
					
					// For android, we are showing a native screen with videoplayer
					// Reason: https://github.com/flutter/flutter/issues/139971#issuecomment-1851864013
					if(component.$root.device.app && component.$root.device.android) {						
						component.$helper.sendAppMessage({
							'action': 'showVideoPlayer',
							'fileData': {
								'url': component.player.currentSrc(),
								'currentTime': currentTime
							}
						})
					} else {
						if (component.player.isFullscreen()) {
							component.player.exitFullscreen();
						} else {
							component.player.requestFullscreen();
						}
					}
				}
			});
			
			// Set icon
			myButton.setIcon('fullscreen-enter');
		},
		toggleInfoBox: function(content) {
			this.fallbackInfo = content;
		},
		getWeightedVastUrl: function() { 
			var component = this;
			let totalWeight = 0;
			for (const url in component.adsVastUrls) {
				totalWeight += component.adsVastUrls[url].weight;
			}
		
			let randomNum = Math.random() * totalWeight;
		
			for (const url in component.adsVastUrls) {
				randomNum -= component.adsVastUrls[url].weight;
				if (randomNum <= 0) {
					return component.adsVastUrls[url].url;
				}
			}
		},		
		addExtraParameters: function(url) { 
		
			var app = this.$root;
			var device = app.device;
			var component = this;
			var adUrl = url;
			
			if(!url) {
				return;
			}
 
			 // Set default parameters
			 var csid = 'nieuwnieuws_desktop';
			 var consentString = app.consent;
			 var pvrn = Math.floor(Math.random() * 100000000);
			 var vprn = Math.floor(Math.random() * 1000000000)
			 var vdur = component.adsVdur;
						 
			 // Update csid based on platform type
			 if(device.app) {
				 csid = (device.android) ? 'nieuwnieuws_inapp' : 'nieuwnieuws_inapp';
			 } else if(device.mobile) {
				 csid = (device.android) ? 'nieuwnieuws_mweb' : 'nieuwnieuws_mweb';
			 }	
					
			// Update query params 
			 adUrl = component.$helper.updateQueryStringParameter(adUrl, 'csid', csid);
			 adUrl = component.$helper.updateQueryStringParameter(adUrl, 'pvrn', pvrn);
			 adUrl = component.$helper.updateQueryStringParameter(adUrl, 'vprn', vprn);
			 adUrl = component.$helper.updateQueryStringParameter(adUrl, 'vdur', vdur);
			 
			 // Replace with consent			
			 adUrl = adUrl.replace('%_FW_GDPR_CONSENT%', consentString);
			 adUrl = adUrl.replace('%_FW_GDPR_CONSENT_A%', consentString);
			 adUrl = adUrl.replace('%_FW_GDPR_CONSENT_B%', consentString);
			 adUrl = adUrl.replace('%_FW_GDPR_CONSENT_C%', consentString);
			 
			// Custom VAST waterfall, add consent, provider
			//if(component.$helper.getURLParameter('waterfall') == 'true') {
			if(adUrl.includes('www.nieuwnieuws.nl') || adUrl.includes('dev.nieuwnieuws.nl')) {
				adUrl = component.$helper.updateQueryStringParameter(adUrl, 'consent', consentString);
				
				var provider = component.$helper.getURLParameter('provider')
				adUrl = component.$helper.updateQueryStringParameter(adUrl, 'provider', provider);
			}
			
			component.$logger('app/components/video-player/' + component.id + '/update-ad-url: ' + adUrl);
			
			return adUrl;
		},
		startConsentCheck: function() {
			
			var app = this.$root;
			var component = this;
			var maxChecks = (component.isEmbed == true) ? 0 : 10; // stop check for embed codes
						
			component.$logger('app/components/video-player/' + component.id + '/start-consent-check/max-check: ' + maxChecks);
		 
			// Set up polling every second
			component.consentInterval = setInterval(() => {
				
				component.consentAttempts++;
				
				if (app.consent && app.consent.length > 100) {
					
					// Init player
					component.init();
			
					// Clear interval
					component.clearConsentInterval();
					
				} else if (component.consentAttempts >= maxChecks) {
					
					// Give up consent, init player
					component.init();
					
					// Clear interval
					component.clearConsentInterval();
					
					// reset attempts		
					component.consentAttempts = 0;		
				}
			}, 500);
					
		},
		clearConsentInterval: function() {
			var component = this;
			clearInterval(component.consentInterval);
			component.consentInterval = null;
		},
	}
}
</script>

<style scoped>
/* player component */
.component.video-player {
	overflow: hidden;
	min-height: 180px;
}
.component.video-player .video {
	background: #20202C;
	border-radius: 8px;
	overflow: hidden;
}
.component.video-player .poster {
	min-height: 180px; 
	max-width: 100%; 
	overflow: hidden;
	position: relative;
	background: #20202C;
	border-radius: 8px;	
	margin: auto;
}
.component.video-player .poster img {
}
.component.video-player.gif {
}
.component.video-player .meta-data {
}

/* IMA */
.component.video-player .video-js .ima-ad-container .ima-controls-div {
	overflow: unset;
}
.component.video-player .video-js .ima-ad-container .ima-controls-div .ima-progress-div {
	background-color: #2a05e4 !important;
}
.ima-countdown-div {
	padding-left: 4px !important;
	padding-bottom: 4px !important;
}
</style>
